<!--
  Topbar Component
  Global search, household switcher, user menu.
-->
<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { navigate, useLocation } from 'svelte-routing';
  import { Search, CalendarClock } from 'lucide-svelte';
  import { authStore } from '../../auth';
  import { activeHouseholdStore } from '../../stores/activeHouseholdStore';
  import { householdsStore } from '../../stores/householdsStore';
  import { followupsStore } from '../../stores/followupsStore';
  import { isFollowupOverdue } from '../../followups';
  import Tooltip from '../ui/Tooltip.svelte';
  import UserMenu from './UserMenu.svelte';

  const location = useLocation();
  $: isFollowupsRoute = $location.pathname === '/followups';

  let searchQuery = '';

  // Spec 120 E5: on /browse the field carries the active search word from the URL — removing
  // the search chip in the view therefore empties the field as well.
  $: if ($location.pathname === '/browse') {
    searchQuery = new URLSearchParams($location.search).get('search') ?? '';
  }

  function handleSearch(e: KeyboardEvent) {
    if (e.key === 'Enter' && searchQuery.trim()) {
      navigate(`/browse?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  }

  function handleHouseholdChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    activeHouseholdStore.setActiveHousehold(select.value);
  }

  // Spec 086/E2-E4, F1: badge is derived from the shared store, not a household-scoped fetch
  // (E3). Loaded exactly once, by `App.svelte`'s `loadInitialStores` — Topbar only subscribes.
  // (A second load-on-mount here duplicated the fetch every time Topbar mounted.)
  $: openFollowupsCount = $followupsStore.length;
  $: overdueFollowupsCount = $followupsStore.filter((f) => isFollowupOverdue(f.dueDate)).length;
  // Badge number matches its colour: red = how many are overdue, orange = how many are open.
  $: hasOverdueFollowups = overdueFollowupsCount > 0;
  $: badgeCount = hasOverdueFollowups ? overdueFollowupsCount : openFollowupsCount;
  // Spec 107 E3: muted without open follow-ups; the route highlight always wins.
  $: followupsIconClass = isFollowupsRoute
    ? 'text-accent'
    : openFollowupsCount > 0
      ? 'text-text-secondary hover:text-text'
      : 'text-text-muted hover:text-text';
  $: followupsBadgeText = badgeCount > 99 ? '99+' : String(badgeCount);
  $: followupsAriaLabel =
    openFollowupsCount === 0
      ? $_('nav.followups_aria_none')
      : $_('nav.followups_aria', {
          values: { open: openFollowupsCount, overdue: overdueFollowupsCount },
        });

  function handleFollowupsClick(e: MouseEvent) {
    if (e.ctrlKey || e.metaKey || e.button === 1) return;
    e.preventDefault();
    navigate('/followups');
  }
</script>

<header class="h-14 flex items-center gap-4 px-5 border-b border-border bg-bg-alt/50 shrink-0">
  <!-- Global Search -->
  <div class="flex-1 max-w-lg relative">
    <Search
      class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted pointer-events-none"
      strokeWidth={1.5}
    />
    <input
      data-testid="topbar-search-input"
      type="search"
      placeholder={$_('nav.search_placeholder')}
      bind:value={searchQuery}
      on:keydown={handleSearch}
      class="w-full h-9 pl-9 pr-3 bg-surface text-text text-sm rounded border border-border
        hover:border-border-strong focus:border-accent focus:outline-none
        transition-colors placeholder:text-text-muted"
    />
  </div>

  <div class="flex items-center gap-3 ml-auto">
    <!-- Household Scope Switcher — neutral container; only the active scope value is accented (#258) -->
    {#if $authStore && $householdsStore.length > 0}
      <div
        class="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border bg-surface hover:bg-surface-hover transition-colors"
      >
        <span class="text-xs font-medium text-text-muted whitespace-nowrap select-none">
          {$_('household.scope_label')}:
        </span>
        <select
          value={$activeHouseholdStore}
          on:change={handleHouseholdChange}
          class="h-7 pl-1 pr-6 text-sm font-semibold bg-transparent text-accent focus:outline-none
            cursor-pointer appearance-none border-none [color-scheme:dark]
            bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2214%22%20height%3D%2214%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23555863%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22/%3E%3C/svg%3E')]
            bg-no-repeat bg-[position:right_2px_center]"
          aria-label={$_('household.switcher_label')}
        >
          {#each $householdsStore as h (h.id)}
            <option value={h.id}>{h.name || h.id}</option>
          {/each}
        </select>
      </div>
    {/if}

    <!-- Wiedervorlagen (Spec 086/E2): always visible when logged in, even with count 0. -->
    {#if $authStore}
      <Tooltip text={followupsAriaLabel} position="bottom">
        <a
          href="/followups"
          data-testid="topbar-followups-icon"
          aria-label={followupsAriaLabel}
          on:click={handleFollowupsClick}
          aria-current={isFollowupsRoute ? 'page' : undefined}
          class="shrink-0 flex items-center justify-center w-11 h-11 rounded hover:bg-surface-hover transition-colors {followupsIconClass}"
        >
          <span class="relative flex">
            <CalendarClock class="w-5 h-5" strokeWidth={1.5} />
            {#if openFollowupsCount > 0}
              <span
                data-testid="topbar-followups-badge"
                data-overdue={hasOverdueFollowups}
                class="absolute -top-3 -right-3.5 min-w-4 h-4 px-1 rounded-full text-[10px] font-semibold leading-4 text-center {hasOverdueFollowups
                  ? 'bg-error text-bg'
                  : 'bg-warning text-bg'}"
              >
                {followupsBadgeText}
              </span>
            {/if}
          </span>
        </a>
      </Tooltip>
    {/if}

    <!-- User Menu -->
    {#if $authStore}
      <UserMenu />
    {/if}
  </div>
</header>
